/**
 * Normalizes text used as a human-language search key.
 *
 * This is deliberately narrower than fuzzy matching: it reconciles equivalent
 * French typography produced by Word and LLMs, but always keeps a real space
 * between words. Removing spaces would make a one-word query such as “Lacour”
 * match “la Cour”, which is unsafe for document mutations.
 *
 * Accents are folded: a French act writes “COMPARAÎTRE” and “COMPARAITRE” alike in capitals, and the agent types
 * “comparaitre”. Kept significant, that search found nothing and the agent concluded the search itself was broken
 * (production conversation, 2026-10-11). A fold that makes two blocks match is handled where it matters: a destructive
 * action refuses an ambiguous selector and lists its candidates.
 */
export function normalizeSearchText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    // French typography commonly uses NBSP/narrow NBSP before double punctuation.
    .replace(/[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/gu, ' ')
    // Invisible separators must remain boundaries, never disappear and join words.
    // eslint-disable-next-line no-misleading-character-class -- each code point is matched alone; U+200D is listed as a character, not used as a joiner
    .replace(/[\u200B\u200C\u200D\uFEFF]/gu, ' ')
    .replace(/[\u2010-\u2015]/gu, '-')
    .replace(/[\u2018\u2019\u02BC]/gu, "'")
    .replace(/[\u201C\u201D]/gu, '"')
    // Collapse only runs: a word boundary stays a word boundary.
    .replace(/\s+/gu, ' ')
    .trim()
    .toLocaleLowerCase('fr-FR');
}
